import React, { useState } from 'react';
import styled from 'styled-components';

import { Row, Col, Button, Table, Alert } from 'components/bootstrap';
import { ConfirmDialog, Icon, NoSearchResult, Spinner } from 'components/common';
import useSendTelemetry from 'logic/telemetry/useSendTelemetry';
import useLocation from 'routing/useLocation';
import { getPathnameWithoutId } from 'util/URLUtils';
import { TELEMETRY_EVENT_TYPE } from 'telemetry/Constants';

import EditConnectorModal from './EditConnectorModal';
import useDataLakeConnectors, { deleteDataLakeConnector } from './useDataLakeConnectors';
import DataLakeConnectorsActions from './DataLakeConnectorActions';
import DataLakeConnectorStreamCell from './DataLakeConnectorStreamCell';
import DataLakeConnectorCreateInputModal from './DataLakeConnectorCreateInputModal';
import type { AthenaConnectorCredentials } from './useDataLakeConnectors';

const StyledButton = styled(Button)`
  float: right;
`;

const DataLakeConnectorsList = () => {
  const { data: connectors, refetch, isLoading } = useDataLakeConnectors();
  const [showConnectorModal, setShowConnectorModal] = useState<boolean>(false);
  const [connectorIdToEdit, setConnectorIdToEdit] = useState<string | undefined>(undefined);
  const [connectorToDelete, setConnectorToDelete] = useState<AthenaConnectorCredentials | undefined>(undefined);
  const [connectorIdForInputCreation, setConnectorIdForInputCreation] = useState<string | undefined>(undefined);
  const sendTelemetry = useSendTelemetry();
  const { pathname } = useLocation();

  const handleEditConnector = (id: string) => {
    setConnectorIdToEdit(id);
    setShowConnectorModal(true);
  };

  const handleCloseEditConnectorModal = () => {
    setConnectorIdToEdit(undefined);
    setShowConnectorModal(false);
  };

  const handleDeleteConnector = async () => {
    await deleteDataLakeConnector(connectorToDelete?.id);
    await refetch();
    setConnectorToDelete(undefined);
  };

  const handleAddConnectorClick = () => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.DATALAKE.CONNECTORS.ADD_CONNECTOR_BUTTON_CLICKED, {
      app_pathname: getPathnameWithoutId(pathname),
    });

    setShowConnectorModal(true);
  };

  return (
    <>
      <Row>
        <Col md={6}>
          <h2>AWS Security Lake Connectors</h2>
        </Col>
        <Col md={6}>
          <StyledButton onClick={handleAddConnectorClick} bsStyle="primary">
            <Icon name="add" /> Add Connector
          </StyledButton>
        </Col>
      </Row>
      <Alert bsStyle="info">
        This table contains Connectors to external Amazon Security Lakes. Note that each Connector is restricted to a
        single AWS region of a single AWS account; to cover multiple regions or accounts, multiple Connectors will be
        required.
      </Alert>
      {showConnectorModal && (
        <EditConnectorModal
          connectorId={connectorIdToEdit}
          onRefetch={refetch}
          onCancel={handleCloseEditConnectorModal}
        />
      )}
      <Table>
        <thead>
          <tr>
            <th>Name</th>
            <th>AWS Access Key</th>
            <th>AWS Region</th>
            <th>S3 Output Bucket</th>
            <th>Stream</th>
            <th className="text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {connectors?.map((connector) => (
            <tr key={connector.id} data-testid={`table-row-${connector.id}`}>
              <td>{connector.data_lake_name}</td>
              <td>{connector.aws_access_key}</td>
              <td>{connector.aws_region}</td>
              <td>{connector.s3_output_bucket}</td>
              <DataLakeConnectorStreamCell stream_id={connector.stream_id} />
              <td>
                <DataLakeConnectorsActions
                  dataLakeId={connector.id}
                  dataLakeTitle={connector.data_lake_name}
                  onEdit={() => handleEditConnector(connector.id)}
                  onDelete={() => setConnectorToDelete(connector)}
                  onCreateInput={() => setConnectorIdForInputCreation(connector.id)}
                />
              </td>
            </tr>
          ))}

          {!connectors?.length && (
            <tr>
              <td colSpan={6}>
                {isLoading ? <Spinner /> : <NoSearchResult>No Data Lake Connectors have been found.</NoSearchResult>}
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      {connectorToDelete && (
        <ConfirmDialog
          title="Delete Connector"
          show
          onConfirm={handleDeleteConnector}
          onCancel={() => setConnectorToDelete(undefined)}>
          <p>
            Deleting this connector will also permanently remove all linked retrievals and the retrieval stream.
          </p>
          <p>
            Are you sure you want to delete the data lake connector{' '}
            <b>
              <i>{connectorToDelete?.data_lake_name}</i>
            </b>{' '}
            ?
          </p>
        </ConfirmDialog>
      )}
      {connectorIdForInputCreation && (
        <DataLakeConnectorCreateInputModal
          connectorId={connectorIdForInputCreation}
          onClose={() => setConnectorIdForInputCreation(undefined)}
        />
      )}
    </>
  );
};

export default DataLakeConnectorsList;
